// src/Pages/Admin/AllCategories.js
import React, { useState, useEffect } from "react";
import { db, storage } from "../../../firebase";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import {
  ref,
  deleteObject,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import "./AllCategories.css";

const AllCategories = () => {
  const [categories, setCategories] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  // Edit popup states
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [editImage, setEditImage] = useState(null);
  const [editPreview, setEditPreview] = useState("");

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "categories"));
      const data = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(data);
    } catch (err) {
      console.error("Error fetching categories:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const toggleExpand = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  // Delete Category
  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete category "${name}"?`)) {
      try {
        const catToDelete = categories.find((cat) => cat.id === id);

        // Delete image if exists
        if (catToDelete?.imagePath) {
          try {
            const imageRef = ref(storage, catToDelete.imagePath);
            await deleteObject(imageRef);
            console.log("Deleted image:", catToDelete.imagePath);
          } catch (err) {
            console.warn("Error deleting image:", err.message);
          }
        }

        // Delete category doc
        await deleteDoc(doc(db, "categories", id));
        setCategories(categories.filter((cat) => cat.id !== id));
      } catch (err) {
        console.error("Error deleting category:", err);
      }
    }
  };

  // Open edit popup
  const startEditing = (cat) => {
    setEditing(cat.id);
    setEditName(cat.name);
    setEditPreview(cat.imageUrl || "");
    setEditImage(null);
  };

  // Handle new image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditImage(file);
      setEditPreview(URL.createObjectURL(file));
    }
  };

  // Save changes
  const handleSave = async () => {
    try {
      const catRef = doc(db, "categories", editing);
      let updatedData = { name: editName };

      // If new image selected
      if (editImage) {
        // Delete old image if exists
        const oldCat = categories.find((cat) => cat.id === editing);
        if (oldCat?.imagePath) {
          try {
            await deleteObject(ref(storage, oldCat.imagePath));
          } catch (err) {
            console.warn("Error deleting old image:", err.message);
          }
        }

        // Upload new image
        const imagePath = `categories/${Date.now()}_${editImage.name}`;
        const imageRef = ref(storage, imagePath);
        await uploadBytes(imageRef, editImage);
        const downloadURL = await getDownloadURL(imageRef);

        updatedData.imageUrl = downloadURL;
        updatedData.imagePath = imagePath;
      }

      // Update Firestore
      await updateDoc(catRef, updatedData);

      // Update local state
      setCategories(
        categories.map((cat) =>
          cat.id === editing ? { ...cat, ...updatedData } : cat
        )
      );

      // Close popup
      setEditing(null);
      setEditName("");
      setEditImage(null);
      setEditPreview("");
    } catch (err) {
      console.error("Error updating category:", err);
    }
  };

  if (loading) return <p className="loading">Loading categories...</p>;

  return (
    <div className="all-categories-page">
      <h2 className="page-title">All Categories</h2>

      <div className="category-list">
        {categories.map((cat) => (
          <div key={cat.id} className="category-card">
            <div
              className="category-header"
              onClick={() => toggleExpand(cat.id)}
            >
              <h3>{cat.name}</h3>

              <div className="actions">
                <button
                  className="edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    startEditing(cat);
                  }}
                >
                  ✏️ Edit
                </button>
                <button
                  className="delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(cat.id, cat.name);
                  }}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>

            {expanded === cat.id && (
              <div className="subcategory-list">
                {cat.subCategories && cat.subCategories.length > 0 ? (
                  cat.subCategories.map((sub, i) => (
                    <div key={i} className="subcategory-item">
                      <span>{sub}</span>
                    </div>
                  ))
                ) : (
                  <p className="no-sub">No subcategories</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Edit Popup */}
      {editing && (
        <div className="popup-overlay">
          <div className="popup">
            <h3>Edit Category</h3>

            <label>Name:</label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />

            <label>Image:</label>
            {editPreview && (
              <img
                src={editPreview}
                alt="preview"
                className="preview-img"
                onClick={() =>
                  document.getElementById("editImageInput").click()
                }
              />
            )}
            <input
              type="file"
              id="editImageInput"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageChange}
            />

            <div className="popup-actions">
              <button onClick={handleSave} className="save-btn">
                ✅ Update
              </button>
              <button onClick={() => setEditing(null)} className="cancel-btn">
                ❌ Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllCategories;
